import React, { useState, useEffect, useCallback } from 'react';
import PageLoader from '../components/PageLoader';
import { api } from '../App';
import { getSpeltakImage } from '../utils/speltakImages';
import '../styles/AdminDashboardOverzichtPage.css';

function getSpeltakStatus(speltak) {
  if (!speltak.heeft_bestelling) {
    return { label: 'Nog niet besteld', className: 'status-niet' };
  }

  const hasOpen = speltak.bestellingen.some((o) => o.status === 0);
  if (hasOpen) {
    return { label: 'Open', className: 'status-open' };
  }

  return { label: 'Gesloten', className: 'status-gesloten' };
}

function AdminDashboardOverzichtPage({ user, onBack, onLogout, onSelectSpeltak }) {
  const [datums, setDatums] = useState([]);
  const [selectedDatum, setSelectedDatum] = useState('');
  const [overzicht, setOverzicht] = useState(null);
  const [loadingDatums, setLoadingDatums] = useState(true);
  const [loadingOverzicht, setLoadingOverzicht] = useState(false);
  const [error, setError] = useState('');

  const loadOverzicht = useCallback(async (datumId) => {
    if (!datumId) {
      setOverzicht(null);
      return;
    }

    try {
      setLoadingOverzicht(true);
      setError('');
      const response = await api.get(`/index.php?action=adminoverzicht&datum_id=${datumId}`);
      setOverzicht(response.data);
      setLoadingOverzicht(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij laden van overzicht');
      setOverzicht(null);
      setLoadingOverzicht(false);
    }
  }, []);

  const loadDatums = useCallback(async () => {
    try {
      const response = await api.get('/index.php?action=admindatums');
      const loadedDatums = response.data;
      setDatums(loadedDatums);
      if (loadedDatums.length > 0) {
        const firstId = String(loadedDatums[0].ID);
        setSelectedDatum(firstId);
        loadOverzicht(firstId);
      }
      setLoadingDatums(false);
    } catch (err) {
      setError('Fout bij laden van leveringsdatums');
      setLoadingDatums(false);
    }
  }, [loadOverzicht]);

  useEffect(() => {
    loadDatums();
  }, [loadDatums]);

  const handleDatumChange = (e) => {
    const datumId = e.target.value;
    setSelectedDatum(datumId);
    loadOverzicht(datumId);
  };

  if (loadingDatums) {
    return <PageLoader />;
  }

  return (
    <div className="admin-overzicht-container">
      <header className="admin-header">
        <button type="button" onClick={onBack} className="back-button">← Terug</button>
        <h1>Dashboard-overzicht</h1>
        <button type="button" onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="admin-main">
        <div className="overzicht-wrapper">
          <div className="datum-selector">
            <label htmlFor="overzicht-datum">Leveringsdatum</label>
            <select
              id="overzicht-datum"
              className="datum-select"
              value={selectedDatum}
              onChange={handleDatumChange}
            >
              <option value="">Kies een leveringsdatum</option>
              {datums.map((d) => (
                <option key={d.ID} value={d.ID}>{d.formatted}</option>
              ))}
            </select>
          </div>

          {error && <p className="error-message">{error}</p>}

          {loadingOverzicht && <PageLoader />}

          {!loadingOverzicht && overzicht && (
            <>
              <div className="overzicht-summary">
                <div className="summary-card">
                  <span className="summary-value">
                    {overzicht.aantal_besteld}/{overzicht.totaal_speltakken}
                  </span>
                  <span className="summary-label">Speltakken besteld</span>
                </div>
                <div className="summary-card">
                  <span className="summary-value">{overzicht.aantal_nog_niet}</span>
                  <span className="summary-label">Nog niet besteld</span>
                </div>
                <div className="summary-card">
                  <span className="summary-value">{overzicht.totaal_regels}</span>
                  <span className="summary-label">Totaal regels</span>
                </div>
                <div className="summary-card">
                  <span className="summary-value">
                    {overzicht.aantal_open} open / {overzicht.aantal_gesloten} gesloten
                  </span>
                  <span className="summary-label">Bestellingen</span>
                </div>
              </div>

              <p className="overzicht-leverdatum">
                Overzicht voor <strong>{overzicht.leverdatum}</strong>
              </p>

              {overzicht.notities?.length > 0 && (
                <section className="admin-notities-sectie" aria-labelledby="admin-notities-titel">
                  <div className="admin-notities-header">
                    <h2 id="admin-notities-titel">
                      <span className="admin-notities-icon" aria-hidden="true">📝</span>
                      Notities van speltakken
                    </h2>
                    <span className="admin-notities-badge">{overzicht.aantal_notities}</span>
                  </div>
                  <p className="admin-notities-intro">
                    Opmerkingen bij bestellingen voor deze leveringsdatum — controleer deze vóór de inkoop.
                  </p>
                  <div className="admin-notities-lijst">
                    {overzicht.notities.map((item) => {
                      const image = getSpeltakImage(item.speltak_naam);
                      return (
                        <article
                          key={`notitie-${item.bestelling_id}`}
                          className="admin-notitie-kaart"
                          onClick={() => onSelectSpeltak(item.speltak_id, item.speltak_naam)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onSelectSpeltak(item.speltak_id, item.speltak_naam);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="admin-notitie-kaart-header">
                            <div className="admin-notitie-speltak">
                              {image && (
                                <img
                                  src={image}
                                  alt=""
                                  className="admin-notitie-logo"
                                />
                              )}
                              <div>
                                <strong className="admin-notitie-speltaknaam">{item.speltak_naam}</strong>
                                <span className="admin-notitie-meta">
                                  Bestelling #{item.bestelling_id} · {item.besteldatum}
                                </span>
                              </div>
                            </div>
                            <span className={`status status-${item.status === 0 ? 'open' : 'gesloten'}`}>
                              {item.status === 0 ? 'Open' : 'Gesloten'}
                            </span>
                          </div>
                          <blockquote className="admin-notitie-tekst">{item.notitie}</blockquote>
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}

              <div className="speltak-overzicht-grid">
                {overzicht.speltakken.map((speltak) => {
                  const status = getSpeltakStatus(speltak);
                  const image = getSpeltakImage(speltak.speltak_naam);

                  return (
                    <div
                      key={speltak.speltak_id}
                      className={`speltak-overzicht-card ${status.className}`}
                      onClick={() => onSelectSpeltak(speltak.speltak_id, speltak.speltak_naam)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onSelectSpeltak(speltak.speltak_id, speltak.speltak_naam);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="speltak-overzicht-header">
                        {image && (
                          <img
                            src={image}
                            alt={speltak.speltak_naam}
                            className="speltak-overzicht-logo"
                          />
                        )}
                        <h3>{speltak.speltak_naam}</h3>
                      </div>
                      <span className={`status-badge ${status.className}`}>{status.label}</span>
                      {speltak.heeft_bestelling ? (
                        <div className="speltak-overzicht-details">
                          <p>
                            {speltak.aantal_bestellingen} bestelling
                            {speltak.aantal_bestellingen !== 1 ? 'en' : ''}
                          </p>
                          <p>{speltak.aantal_regels} regel{speltak.aantal_regels !== 1 ? 's' : ''}</p>
                          {speltak.heeft_notitie && (
                            <p className="speltak-notitie-indicator">📝 Bevat notitie</p>
                          )}
                        </div>
                      ) : (
                        <p className="speltak-overzicht-empty">Geen bestelling geplaatst</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loadingOverzicht && !overzicht && !error && selectedDatum && (
            <p className="empty-message">Geen gegevens beschikbaar voor deze datum.</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboardOverzichtPage;
